export function lp(p, lang) {
  if (!p) return p;
  if (lang !== "en") {
    return {
      name: p.name, tagline: p.tagline, description: p.description,
      category: p.category, sizeLabel: (s) => s.label,
    };
  }
  return {
    name: p.name_en || p.name,
    tagline: p.tagline_en || p.tagline,
    description: p.description_en || p.description,
    category: p.category_en || p.category,
    sizeLabel: (s) => s.label_en || s.label,
  };
}

export function itemName(item, lang) {
  return lang === "en" && item.name_en ? item.name_en : item.name;
}

// Texte alternatif SEO enrichi (bilingue) pour chaque photo de produit :
// nom + accroche/catégorie + mention gravure + marque
export function productAlt(p, lang) {
  if (!p) return "";
  const l = lp(p, lang);
  const parts = [l.name];
  if (l.tagline) parts.push(l.tagline);
  else if (l.category) parts.push(l.category);
  parts.push(lang === "en" ? "custom engraving — Mr Trophée" : "gravure personnalisée — Mr Trophée");
  return parts.filter(Boolean).join(" — ");
}

export function itemSizeLabel(item, lang) {
  const size = lang === "en" && item.size_label_en ? item.size_label_en : item.size_label;
  const finish = lang === "en" && item.finish_label_en ? item.finish_label_en : item.finish_label;
  return finish ? `${size} · ${finish}` : size;
}

// numéro de catalogue dans le libellé (« Catalogue nº 4.11 — Curling » → « 4.11 »,
// gère aussi l'anglais « Catalogue No. 4.11 — Curling »)
export const catNoOf = (label) =>
  (label.match(/n[º°o.]*[\s.]*(\d+(?:\.\d+)?)/i) || [])[1] || "";

// Noms des catalogues fournisseurs (identifie clairement la provenance d'un produit)
export const CATALOGUE_NAMES = {
  "1.0": "Awards of Distinction",
  "1.2": "Awards of Distinction — Brochures",
  "1.3": "Awards of Distinction — Nouveautés PDU",
  "1.4": "Awards of Distinction — Academic",
  "1.5": "Awards of Distinction — Baseball",
  "1.6": "Awards of Distinction — Basketball",
  "1.7": "Awards of Distinction — Football",
  "1.8": "Awards of Distinction — Fusion",
  "1.9": "Awards of Distinction — Golf",
  "1.10": "Awards of Distinction — Hockey",
  "1.11": "Awards of Distinction — Soccer",
  "1.99": "Awards of Distinction — Pièces (components)",
  "2.0": "Tromart",
  "3.0": "Marco Awards",
  "4.0": "Caldwell",
  "4.1": "Caldwell — Academic",
  "4.2": "Caldwell — Badminton",
  "4.3": "Caldwell — Ballon-balai",
  "4.4": "Caldwell — Baseball",
  "4.5": "Caldwell — Basketball",
  "4.6": "Caldwell — Billard",
  "4.7": "Caldwell — Quilles",
  "4.8": "Caldwell — Cheerleading",
  "4.9": "Caldwell — Échecs",
  "4.10": "Caldwell — Entraîneur",
  "4.11": "Caldwell — Curling",
  "4.12": "Caldwell — Danse",
  "4.13": "Caldwell — Fléchettes",
  "4.14": "Caldwell — Patinage artistique",
  "4.15": "Caldwell — Football",
  "4.16": "Caldwell — Golf",
  "4.17": "Caldwell — Hockey",
  "4.18": "Caldwell — Équestre",
  "4.19": "Caldwell — Crosse",
  "4.20": "Caldwell — Arts martiaux",
  "4.21": "Caldwell — Musique",
  "4.22": "Caldwell — Course automobile",
  "4.23": "Caldwell — Soccer",
  "4.24": "Caldwell — Natation",
  "4.25": "Caldwell — Tennis",
  "4.26": "Caldwell — Athlétisme",
  "4.27": "Caldwell — Volleyball",
  "5.0": "(U)",
  "6.0": "Mr Trophée Boutique",
};

export function catalogueLabel(no) {
  if (!no) return "";
  const name = CATALOGUE_NAMES[no];
  if (!name) return `Catalogue nº ${no}`;
  return name.startsWith("(") ? `Catalogue nº ${no} ${name}` : `Catalogue nº ${no} — ${name}`;
}

// Familles de catalogues (regroupement dans les menus déroulants admin + boutique)
export const FAMILY_NAMES = {
  "1": "Awards of Distinction",
  "2": "Tromart",
  "3": "Marco Awards",
  "4": "Caldwell",
  "5": "Urbano (articles promotionnels)",
  "6": "Mr Trophée Boutique",
};

export const catalogueFamily = (no) => (no || "").split(".")[0];

// Étiquette courte pour les options groupées : « 4.17 — Hockey » (sans « Caldwell — »)
export function catalogueShortLabel(no) {
  const name = CATALOGUE_NAMES[no];
  if (!name) return no;
  const sub = name.split(" — ")[1];
  return `${no} — ${sub || name.replace("(U)", "Urbano")}`;
}

// Sports filtrables (boutique + admin) — le filtrage est fait par le serveur
// (mots-clés multi-catalogues + sous-catalogues Caldwell du sport)
export const SPORTS = [
  { id: "hockey", fr: "Hockey", en: "Hockey" },
  { id: "soccer", fr: "Soccer", en: "Soccer" },
  { id: "baseball", fr: "Baseball", en: "Baseball" },
  { id: "basketball", fr: "Basketball", en: "Basketball" },
  { id: "football", fr: "Football", en: "Football" },
  { id: "golf", fr: "Golf", en: "Golf" },
  { id: "volleyball", fr: "Volleyball", en: "Volleyball" },
  { id: "natation", fr: "Natation", en: "Swimming" },
  { id: "tennis", fr: "Tennis", en: "Tennis" },
  { id: "curling", fr: "Curling", en: "Curling" },
  { id: "badminton", fr: "Badminton", en: "Badminton" },
  { id: "crosse", fr: "Crosse", en: "Lacrosse" },
  { id: "arts-martiaux", fr: "Arts martiaux", en: "Martial arts" },
  { id: "quilles", fr: "Quilles", en: "Bowling" },
  { id: "patinage", fr: "Patinage artistique", en: "Figure skating" },
  { id: "danse", fr: "Danse", en: "Dance" },
  { id: "cheerleading", fr: "Cheerleading", en: "Cheerleading" },
  { id: "athletisme", fr: "Athlétisme", en: "Track & field" },
  { id: "equestre", fr: "Équestre", en: "Equestrian" },
  { id: "course-auto", fr: "Course automobile", en: "Racing" },
  { id: "billard", fr: "Billard", en: "Billiards" },
  { id: "flechettes", fr: "Fléchettes", en: "Darts" },
  { id: "echecs", fr: "Échecs", en: "Chess" },
  { id: "musique", fr: "Musique", en: "Music" },
  { id: "ballon-balai", fr: "Ballon-balai", en: "Broomball" },
  { id: "scolaire", fr: "Scolaire", en: "School" },
];

// Pluriel français propre (demande du propriétaire — « les pluriels quand plus de 1 ») :
// plur(1, "article") → "1 article" ; plur(3, "article") → "3 articles" ;
// plur(2, "trophée d'équipe") → "2 trophées d'équipe". 0 et 1 restent au SINGULIER
// en français. 3e argument pour les formes irrégulières : plur(2, "journal", "journaux").
export const plur = (n, sing, pl) => `${n} ${Number(n) > 1 ? (pl || `${sing}s`) : sing}`;
